// Call dock (DESIGN.md "Shell", IA §2): controls exist only while a call does.
//   idle      a quiet line state ("Line ready", "Line offline · Retry")
//   ringing   the caller with Answer / Decline
//   dialling  the party with Hang up
//   live      outside a session, a compact glass live bar: name, mono number,
//             aqua LIVE timer (amber "On hold" while held), Mute M · Hold H ·
//             Transfer T · End Shift+E, and "Focus · F" to open the full Live
//             view once that route exists (navigation.FOCUS_VIEW_PATH)
//   wrap-up   after a dock call ends (not in a session), a compact sheet:
//             outcome keys 1–6, notes, next step, Save (DockWrapSheet)
// Screens with their own call view own the call, so the dock stays quiet
// there. All state and actions come from the existing SipContext and
// IntegratedDialerContext. Ending needs Shift+E here: a stray "e" on a
// workspace page must not hang up on a customer.

import React, { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SipContext from "../../contexts/SipContext";
import SocketContext from "../../contexts/SocketContext";
import IntegratedDialerContext from "../../contexts/IntegratedDialerContext";
import { useAuth } from "../../contexts/AuthContext";
import { NATIVE_MEDIA_STATUS } from "../../services/nativeMediaService";
import { FOCUS_VIEW_PATH, isSessionPath } from "./navigation";
import DockWrapSheet from "./DockWrapSheet";
import { getDockState, setDockState } from "./dockStore";
import {
  FocusIcon,
  MicIcon,
  MicOffIcon,
  PauseIcon,
  PhoneIcon,
  PhoneOffIcon,
  PlayIcon,
  RefreshIcon,
  TransferIcon,
} from "./icons";

export const formatDuration = (seconds = 0) => {
  const total = Math.max(0, Math.floor(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return h ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
};

/** Idle line state from SIP flags; mirrors SipStatusIndicator's reading. */
export const describeLine = (sip, { demo } = {}) => {
  if (demo) return { tone: "idle", label: "Demo · calling off" };
  if (!sip) return { tone: "idle", label: "Line offline" };
  if (sip.nativeCallInfo?.backend === "native_sip") {
    const status = sip.nativeMediaStatus;
    if (status === NATIVE_MEDIA_STATUS.FAILED)
      return { tone: "danger", label: "Native audio unavailable", detail: sip.nativeMediaError };
    if (!sip.isNativeSignalingReady)
      return { tone: "warn", label: "Native signaling reconnecting" };
    if (status === NATIVE_MEDIA_STATUS.CONNECTED)
      return { tone: "live", label: "Native audio connected" };
    return { tone: "warn", label: "Connecting native audio" };
  }
  if (sip.connectionError)
    return { tone: "danger", label: "Line offline", detail: sip.connectionError, retry: true };
  if (sip.isReconnecting) return { tone: "warn", label: "Reconnecting line" };
  if (sip.isConnected && sip.isRegistered) return { tone: "live", label: "Line ready" };
  if (sip.isConnected) return { tone: "warn", label: "Registration pending", retry: true };
  if (sip.isInitialized) return { tone: "warn", label: "Connecting line", retry: true };
  return {
    tone: "idle",
    label: "Line inactive",
    detail: "Browser SIP extension is inactive. Companion-phone calling may still be available.",
  };
};

const EDITABLE = "input, textarea, select, [contenteditable=''], [contenteditable='true']";

/**
 * Single-key shortcuts for the live bar; ignored while typing or in a dialog.
 * Bindings are keyed by the lower-case key, prefixed "shift+" when Shift is
 * held, so "e" and "shift+e" are different bindings.
 */
const useBarKeys = (bindings, enabled) => {
  const ref = useRef(bindings);
  useEffect(() => {
    ref.current = bindings;
  });
  useEffect(() => {
    if (!enabled) return undefined;
    const onKeyDown = (event) => {
      if (event.defaultPrevented || event.repeat) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target;
      if (target instanceof Element && target.closest(EDITABLE)) return;
      if (document.querySelector("[role='dialog'][aria-modal='true']")) return;
      const id = `${event.shiftKey ? "shift+" : ""}${event.key.toLowerCase()}`;
      const handler = ref.current[id];
      if (typeof handler === "function") {
        event.preventDefault();
        handler();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [enabled]);
};

/**
 * A dock control. `wide` shows the label and its key inline ("Mute M"), as
 * the live bar does; `hint` is the visible key and `keys` the
 * aria-keyshortcuts value when they differ ("⇧E" vs "Shift+E").
 */
const DockButton = ({ label, hint, keys, onClick, children, tone, pressed, disabled, wide }) => (
  <button
    type="button"
    className="sg-dock-btn"
    data-tone={tone}
    data-wide={wide || undefined}
    aria-label={label}
    aria-pressed={pressed}
    aria-keyshortcuts={keys || hint}
    data-hint={wide ? undefined : label}
    data-hint-keys={wide ? undefined : hint}
    onClick={onClick}
    disabled={disabled}
  >
    {children}
    {wide && (
      <span className="sg-dock-btn-label" aria-hidden="true">
        {label}
      </span>
    )}
    {wide && hint && (
      <kbd className="sg-dock-key" aria-hidden="true">
        {hint}
      </kbd>
    )}
  </button>
);

/** Snapshot the parts of a call the wrap-up sheet needs after it ends. */
const snapshotCall = (call, sip) => ({
  id: call?.id || call?.callId || null,
  name: call?.name || call?.contactName || null,
  number: call?.number || call?.phoneNumber || call?.phone || sip?.callInfo?.remoteIdentity || null,
  contactId: call?.contactId || null,
  campaignId: call?.campaignId || null,
});

const TransferRow = ({ onTransfer, onCancel }) => {
  const [extension, setExtension] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  useEffect(() => inputRef.current?.focus(), []);
  const submit = async (event) => {
    event.preventDefault();
    if (!extension.trim() || busy) return;
    setBusy(true);
    try {
      await onTransfer(extension.trim());
      onCancel();
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="sg-dock-transfer" onSubmit={submit}>
      <label className="sr-only" htmlFor="dock-transfer-ext">
        Transfer to extension
      </label>
      <input
        id="dock-transfer-ext"
        ref={inputRef}
        className="sg-field"
        inputMode="tel"
        placeholder="Extension or number"
        value={extension}
        onChange={(event) => setExtension(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.stopPropagation();
            onCancel();
          }
        }}
      />
      <button type="submit" className="sg-btn sg-btn-primary sg-btn-sm" disabled={!extension.trim() || busy}>
        {busy ? "Transferring…" : "Transfer"}
      </button>
      <button type="button" className="sg-btn sg-btn-ghost sg-btn-sm" onClick={onCancel}>
        Cancel
      </button>
    </form>
  );
};

const CallDock = () => {
  const sip = useContext(SipContext);
  const dialer = useContext(IntegratedDialerContext);
  const { user } = useAuth() || {};
  const socket = useContext(SocketContext);
  const location = useLocation();
  const navigate = useNavigate();
  const onSession = isSessionPath(location.pathname);
  // Development-only, as before: a quiet second line instead of a toast that
  // covered page content. Production keeps realtime degradation silent.
  const realtimeIssue = import.meta.env.DEV ? socket?.error : null;
  const [retrying, setRetrying] = useState(false);
  const [transferring, setTransferring] = useState(false);
  // A wrap-up waiting for its outcome survives a remount of the shell.
  // It belongs to the user who took the call, never the next sign-in.
  const [wrap, setWrapState] = useState(() => {
    const pending = getDockState().pendingWrap;
    return pending && pending.userId === user?.id ? pending : null;
  });
  const setWrap = (next) => {
    setWrapState(next);
    setDockState({ pendingWrap: next });
  };

  const call = dialer?.currentCallInfo;
  const sipStatus = sip?.callStatus;
  const ringing = sipStatus === "incoming";
  const live = Boolean(dialer?.isInCall);
  const dialling =
    !live &&
    !ringing &&
    (call?.status === "connecting" || ["outgoing", "progress"].includes(sipStatus));
  const party =
    call?.name || call?.contactName || call?.number || sip?.callInfo?.remoteIdentity || "Unknown caller";
  const number = call?.number || call?.phoneNumber || call?.phone || "";

  // Remember the live call so the sheet can name it and save against its id
  // once the context has cleared it.
  const lastCall = useRef(null);
  const lastDuration = useRef(0);
  const wasLive = useRef(false);
  useEffect(() => {
    if (call && (live || dialling || ringing)) {
      const snap = snapshotCall(call, sip);
      const known = Object.fromEntries(Object.entries(snap).filter(([, v]) => v));
      lastCall.current = { ...lastCall.current, ...known };
    }
    if (live) lastDuration.current = dialer?.callDuration || lastDuration.current;
  }, [call, sip, live, dialling, ringing, dialer?.callDuration]);

  useEffect(() => {
    if (live) {
      wasLive.current = true;
      return;
    }
    if (!wasLive.current) return;
    wasLive.current = false;
    setTransferring(false);
    // Inside a session the session's own wrap-up takes the outcome.
    if (!onSession && lastCall.current) {
      setWrap({ call: lastCall.current, duration: lastDuration.current, userId: user?.id });
    }
    lastCall.current = null;
    lastDuration.current = 0;
  }, [live, onSession, user?.id]);

  const retry = async () => {
    if (!sip?.reconnect || retrying) return;
    setRetrying(true);
    try {
      await sip.reconnect();
    } finally {
      setRetrying(false);
    }
  };

  let state = "idle";
  if (ringing) state = "ringing";
  else if (live) state = "live";
  else if (dialling) state = "dialling";
  else if (wrap) state = "wrap";

  const showBar = state === "live" && !onSession;
  const onHold = Boolean(dialer?.isOnHold);
  const focus = FOCUS_VIEW_PATH ? () => navigate(FOCUS_VIEW_PATH) : null;
  const toggleHold = () => (onHold ? dialer?.unholdCall?.() : dialer?.holdCall?.());

  useBarKeys(
    {
      m: () => dialer?.toggleMute?.(),
      h: toggleHold,
      t: () => dialer?.transferCall && setTransferring(true),
      "shift+e": () => dialer?.endCall?.(),
      ...(focus ? { f: focus } : {}),
    },
    showBar && !transferring,
  );

  // Tell the shortcuts sheet which dock keys work right now.
  const mode = state === "live" && !showBar ? "idle" : state;
  useEffect(() => {
    setDockState({ mode });
  }, [mode]);
  useEffect(() => () => setDockState({ mode: "idle" }), []);

  // The session screen owns ringing, live and wrap-up; only the line state
  // shows there.
  // Ringing still shows, since an inbound call can arrive on any screen.
  if (onSession && (state === "live" || state === "dialling")) return null;

  if (state === "wrap") {
    return (
      <DockWrapSheet
        call={wrap.call}
        durationLabel={formatDuration(wrap.duration)}
        onDone={() => {
          setWrap(null);
          dialer?.updateAgentStatus?.("available");
        }}
        onDismiss={() => setWrap(null)}
      />
    );
  }

  const line = describeLine(sip, { demo: user?.demo });

  // A demo workspace already says "calling is disabled" in the banner at the
  // top; an idle pill repeating it only covers page content.
  if (state === "idle" && user?.demo && !realtimeIssue) return null;

  return (
    <section
      // The idle line is part of the shell and never pops in again when a
      // route remounts the Layout; a call arriving does.
      className={`sg-dock${state === "idle" ? "" : " sg-pop"}`}
      data-state={state}
      data-hold={(showBar && onHold) || undefined}
      data-bar={showBar || undefined}
      aria-label="Call dock"
    >
      {state === "idle" && (
        <div className="sg-dock-row" role="status" title={line.detail}>
          <span className="sg-dot" data-tone={line.tone === "idle" ? undefined : line.tone} />
          <span className="sg-dock-title">{line.label}</span>
          {line.retry && (
            <button type="button" className="sg-dock-link" onClick={retry} disabled={retrying}>
              <RefreshIcon size={12} className={retrying ? "motion-safe:animate-spin" : ""} />
              {retrying ? "Retrying" : "Retry"}
            </button>
          )}
        </div>
      )}

      {state === "ringing" && (
        <div className="sg-dock-row" role="status" aria-live="assertive">
          <span className="sg-dot" data-tone="live" data-pulse="true" />
          <div className="sg-dock-party">
            <span className="sg-dock-title">{party}</span>
            <span className="sg-dock-meta">Incoming call</span>
          </div>
          <div className="sg-dock-actions">
            <DockButton label="Decline" tone="danger" onClick={() => sip?.hangupCall?.()}>
              <PhoneOffIcon size={15} />
            </DockButton>
            <DockButton label="Answer" tone="live" onClick={() => sip?.answerCall?.()}>
              <PhoneIcon size={15} />
            </DockButton>
          </div>
        </div>
      )}

      {state === "dialling" && (
        <div className="sg-dock-row" role="status" aria-live="polite">
          <span className="sg-dot" data-tone="warn" />
          <div className="sg-dock-party">
            <span className="sg-dock-title">{party}</span>
            <span className="sg-dock-meta">Calling…</span>
          </div>
          <div className="sg-dock-actions">
            <DockButton label="End" tone="danger" onClick={() => dialer?.endCall?.()}>
              <PhoneOffIcon size={15} />
            </DockButton>
          </div>
        </div>
      )}

      {showBar && (
        <>
          <div className="sg-dock-row sg-dock-live">
            <div className="sg-dock-party" role="status">
              <span className="sg-dock-title">{party}</span>
              {number && number !== party && <span className="sg-dock-number">{number}</span>}
            </div>
            <span
              className="sg-dock-livetag"
              data-tone={onHold ? "hold" : "live"}
              aria-label={`${onHold ? "On hold" : "Live"}, ${formatDuration(dialer?.callDuration)}`}
            >
              <span
                className="sg-dot"
                data-tone={onHold ? "warn" : "live"}
                data-pulse={onHold ? undefined : "true"}
                aria-hidden="true"
              />
              <span aria-hidden="true">{onHold ? "On hold" : "LIVE"}</span>
              <span className="sg-dock-timer" aria-hidden="true">
                {formatDuration(dialer?.callDuration)}
              </span>
            </span>
            <div className="sg-dock-actions" role="group" aria-label="Call controls">
              <DockButton
                wide
                label={dialer?.isMuted ? "Unmute" : "Mute"}
                hint="M"
                pressed={Boolean(dialer?.isMuted)}
                onClick={() => dialer?.toggleMute?.()}
              >
                {dialer?.isMuted ? <MicOffIcon size={15} /> : <MicIcon size={15} />}
              </DockButton>
              <DockButton
                wide
                label={onHold ? "Resume" : "Hold"}
                hint="H"
                pressed={onHold}
                onClick={toggleHold}
              >
                {onHold ? <PlayIcon size={15} /> : <PauseIcon size={15} />}
              </DockButton>
              {dialer?.transferCall && (
                <DockButton
                  wide
                  label="Transfer"
                  hint="T"
                  pressed={transferring}
                  onClick={() => setTransferring((value) => !value)}
                >
                  <TransferIcon size={15} />
                </DockButton>
              )}
              <DockButton
                wide
                label="End"
                hint="⇧E"
                keys="Shift+E"
                tone="danger"
                onClick={() => dialer?.endCall?.()}
              >
                <PhoneOffIcon size={15} />
              </DockButton>
            </div>
            {focus && (
              <button
                type="button"
                className="sg-dock-focus"
                onClick={focus}
                aria-keyshortcuts="F"
                data-hint="Open the Live view"
              >
                <FocusIcon size={14} />
                <span>Focus</span>
                <kbd className="sg-dock-key" aria-hidden="true">
                  F
                </kbd>
              </button>
            )}
          </div>
          {transferring && (
            <TransferRow
              onTransfer={(extension) => dialer.transferCall(extension)}
              onCancel={() => setTransferring(false)}
            />
          )}
        </>
      )}

      {realtimeIssue && (
        <div className="sg-dock-sub" title={realtimeIssue}>
          <span className="sg-dot" data-tone="warn" aria-hidden="true" />
          <span>Live updates reconnecting</span>
        </div>
      )}
    </section>
  );
};

export default CallDock;
